import { useState } from "react";
import axios from "axios";
import Select from "react-select";
import AlertDialog from "./AlertDialog";

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
  }),
};

// Replace SOURCE_OPTIONS with:
const LEAD_SOURCE_CATEGORIES = [
  "Public Tender Platform",
  "Direct Invite",
  "Referral",
  "Outbound Prospecting",
  "Repeat Client",
];

const STAGE_OPTIONS = [
  { value: "Prospecting", label: "Prospecting" },
  { value: "Lost", label: "Lost" },
  { value: "Won", label: "Won" },
];

const SOURCE_OPTIONS = [
  { value: "Rocketreach", label: "Rocketreach" },
  { value: "Urban Toronto", label: "Urban Toronto" },
  { value: "Referral", label: "Referral" },
  { value: "Other", label: "Other" },
];

const COMPANY_TYPE_OPTIONS = [
  { value: "GC", label: "General Contractor" },
  { value: "Developer", label: "Developer" },
  { value: "Architect", label: "Architect" },
  { value: "Other", label: "Other" },
];

export default function ProspectForm({
  setProspects,
  setIsCreating,
  users,
  navigate,
  prospect = null,
  setIsEditingProspect = null,
}) {
  const [formData, setFormData] = useState({
    projectName: prospect?.projectName || "",
    companyName: prospect?.companyName || "",
    email: prospect?.email || "",
    phoneNumber: prospect?.phoneNumber || "",
    contactName: prospect?.contactName || "",
    website: prospect?.website || "",
    companyType: prospect?.companyType || "Other",
    stage: prospect?.stage || "Prospecting",
    sourceCategory: prospect?.sourceCategory || "",
    estimatedProjectValue: prospect?.estimatedProjectValue || "",
  });

  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
    showCancel: false,
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, option) => {
    setFormData((prev) => ({
      ...prev,
      [name]: option ? option.value : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (
      !formData.projectName.trim() ||
      !formData.companyName.trim() ||
      !formData.email.trim()
    ) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Project Name, Company Name, and Email are required.",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const payload = {
        ...formData,
        estimatedProjectValue: parseFloat(formData.estimatedProjectValue) || 0,
      };

      if (prospect?._id) {
        // Update existing prospect
        await axios.put(
          `https://erp-production-72da01c8e651.herokuapp.com/api/prospects/${prospect._id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "Prospect updated successfully!",
          onConfirm: () => {
            setAlert({ ...alert, open: false });
            setIsEditingProspect(false);
          },
        });
      } else {
        // Create new prospect
        const response = await axios.post(
          "https://erp-production-72da01c8e651.herokuapp.com/api/prospects",
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        setProspects((prev) => [response.data, ...prev]);
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "Prospect created successfully!",
          onConfirm: () => {
            setAlert({ ...alert, open: false });
            setIsCreating(false);
            navigate(`/Prospects/${response.data._id}`);
          },
        });
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to save prospect: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-700 p-6 rounded-lg shadow-lg mb-6">
      <h2 className="text-lg font-semibold text-orange-400 mb-4">
        {prospect ? "Edit Prospect" : "Create New Prospect"}
      </h2>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Project Name *
            </label>
            <input
              type="text"
              name="projectName"
              value={formData.projectName}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              placeholder="Enter project name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Company Name *
            </label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              placeholder="Enter company name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              placeholder="Enter email"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Phone Number
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              placeholder="Enter phone number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Contact Name
            </label>
            <input
              type="text"
              name="contactName"
              value={formData.contactName}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              placeholder="Enter contact name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Website
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              placeholder="Enter website URL"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Company Type
            </label>
            <Select
              options={COMPANY_TYPE_OPTIONS}
              value={COMPANY_TYPE_OPTIONS.find(
                (option) => option.value === formData.companyType,
              )}
              onChange={(option) => handleSelectChange("companyType", option)}
              className="mt-1"
              styles={customSelectStyles}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Stage
            </label>
            <Select
              options={STAGE_OPTIONS}
              value={STAGE_OPTIONS.find(
                (option) => option.value === formData.stage,
              )}
              onChange={(option) => handleSelectChange("stage", option)}
              className="mt-1"
              styles={customSelectStyles}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Lead Source
            </label>
            <select
              name="sourceCategory"
              value={formData.sourceCategory}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
            >
              <option value="">Select a source...</option>
              {LEAD_SOURCE_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Estimated Project Value
            </label>
            <input
              type="number"
              name="estimatedProjectValue"
              value={formData.estimatedProjectValue}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              placeholder="Enter estimated value"
              step="0.01"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-4 mt-6">
          <button
            type="button"
            onClick={() => {
              if (prospect) {
                setIsEditingProspect(false);
              } else {
                setIsCreating(false);
              }
            }}
            className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? "Saving..."
              : prospect
                ? "Update Prospect"
                : "Create Prospect"}
          </button>
        </div>
      </form>

      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={alert.onConfirm}
        showCancel={alert.showCancel}
      />
    </div>
  );
}
